import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layouts from "../../config/layouts/Layouts";
import NavbarComponent from "../../components/navbar/Navbar";
import Footer from "../../components/footer/Footer";
import { AUTHORITY_PAGES, CASE_STUDIES } from "../../data/authorityPages";
import Error404 from "../errors/Error404";

function AuthorityContent({ ns, slug, image, sources: rawSources }) {
    const { t } = useTranslation();
    const sections = t(`authority.${ns}.${slug}.sections`, { returnObjects: true });
    const sourceLabels =
        ns === "cases"
            ? t(`authority.cases.${slug}.sourceLabels`, { returnObjects: true, defaultValue: null })
            : null;
    const sources = rawSources?.map(([label, url], i) => [sourceLabels?.[i] || label, url]);

    return (
        <main className="min-h-screen bg-em-bg pb-[calc(7rem+var(--em-footer-h))] pt-32 text-em-text sm:pt-36 md:pt-40">
            <div className="mx-auto max-w-6xl px-5 md:px-8">
                <div className="lg:grid lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-12">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-em-accent">{t(`authority.${ns}.${slug}.eyebrow`)}</p>
                        <h1 className="mt-4 max-w-4xl text-4xl font-black tracking-tight md:text-6xl lg:text-5xl">{t(`authority.${ns}.${slug}.title`)}</h1>
                        <p className="mt-7 max-w-3xl text-lg leading-8 text-em-soft md:text-xl">{t(`authority.${ns}.${slug}.lead`)}</p>
                    </div>
                    {image ? (
                        <div className="relative hidden lg:block">
                            <div className="absolute -inset-6 rounded-[2rem] bg-em-accent/10 blur-3xl" aria-hidden="true" />
                            <img
                                src={image}
                                alt=""
                                className="relative aspect-[4/3] w-full rounded-3xl border border-em-text/10 object-cover shadow-2xl"
                            />
                        </div>
                    ) : null}
                </div>

                <div className="mt-14 grid gap-4 md:grid-cols-3">
                    {sections.map(([title, body]) => (
                        <section key={title} className="rounded-2xl border border-em-text/10 bg-em-card p-6">
                            <h2 className="text-xl font-extrabold text-em-text">{title}</h2>
                            <p className="mt-3 leading-7 text-em-soft">{body}</p>
                        </section>
                    ))}
                </div>

                {sources?.length ? (
                    <section className="mt-14 border-y border-em-text/10 py-8" aria-labelledby="authority-sources">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-em-accent2">{t("authority.evidenciaLabel")}</p>
                        <h2 id="authority-sources" className="mt-3 text-2xl font-black">{t("authority.fuentesTitle")}</h2>
                        <ul className="mt-5 space-y-3">
                            {sources.map(([title, url]) => (
                                <li key={url}><a className="font-semibold text-em-accent hover:text-em-accent2" href={url} target="_blank" rel="noopener noreferrer">{title} ↗</a></li>
                            ))}
                        </ul>
                    </section>
                ) : null}

                <div className="mt-12 flex flex-wrap gap-3">
                    <Link to="/ia-aviacion-seguridad-operacional" className="btn-secondary">{t("authority.viewAviationSpecialty")}</Link>
                    <Link to="/contacto" className="btn-primary">{t("authority.evaluateUseCase")}</Link>
                </div>
            </div>
        </main>
    );
}

export function KnowledgePage() {
    const { slug } = useParams();
    return <PageShell ns="pages" slug={slug} data={AUTHORITY_PAGES[slug]} />;
}

export function CaseStudyPage() {
    const { slug } = useParams();
    return <PageShell ns="cases" slug={slug} data={CASE_STUDIES[slug]} />;
}

function PageShell({ ns, slug, data }) {
    if (!data) return <Error404 />;
    return (
        <Layouts>
            <header><NavbarComponent /></header>
            <AuthorityContent ns={ns} slug={slug} image={data.image} sources={data.sources} />
            <Footer />
        </Layouts>
    );
}
